import { Menu, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { ClosedIssueStatuses, ISSUE_STATUSES, IssuesFilterByOption, IssueStatus, IStatus } from "../../constants";
import { StatusIcon } from "src/components";

interface Props {
  filterByOption: IssuesFilterByOption;
  filterByStatusOption: IssueStatus | undefined;
  onChange: (filterByOption: IssuesFilterByOption, filterByStatusOption: IssueStatus | undefined) => void;
}

const IssuesFilterToggleButton = (props: Props) => {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByChanged(value: IssuesFilterByOption) {
    // The status filter option should only be set after a status option is selected from the submenu
    if (value != null && value !== IssuesFilterByOption.Status) {
      props.onChange(value, undefined);
    }
  }

  function handleFilterByStatusChanged(value: IssueStatus) {
    if (value != null) {
      props.onChange(IssuesFilterByOption.Status, value);
    }
  }

  return (
    <>
      <ToggleButtonGroup
        value={props.filterByOption}
        exclusive
        orientation="vertical"
        variant="menu"
        onChange={(_, value) => handleFilterByChanged(value)}
        sx={{ minWidth: "100%" }}
      >
        <ToggleButton value={IssuesFilterByOption.All}>
          <Typography>All</Typography>
        </ToggleButton>
        <ToggleButton value={IssuesFilterByOption.Status} onClick={(e) => setStatusMenuAnchor(e.currentTarget)}>
          <Typography>{`Status ${
            props.filterByStatusOption
              ? `(${ISSUE_STATUSES.find((s) => s.id === props.filterByStatusOption)?.name})`
              : ""
          }`}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        anchorOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <Typography variant="overline" sx={{ mx: 0.5 }}>
          Active Statuses
        </Typography>
        <ToggleButtonGroup
          value={props.filterByStatusOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => handleFilterByStatusChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          {ISSUE_STATUSES.filter((status) => !ClosedIssueStatuses.includes(status.id)).map((status) => (
            <IssueStatusToggleButton status={status} />
          ))}
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, pt: 0.75 }}>
          Closed Statuses
        </Typography>
        <ToggleButtonGroup
          value={props.filterByStatusOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => handleFilterByStatusChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          {ISSUE_STATUSES.filter((status) => ClosedIssueStatuses.includes(status.id)).map((status) => (
            <IssueStatusToggleButton status={status} />
          ))}
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

const IssueStatusToggleButton = ({ status }: { status: IStatus }) => {
  return (
    <ToggleButton value={status.id}>
      <Stack direction="row" spacing={0.5} alignItems="baseline">
        <StatusIcon status={status} sx={{ alignSelf: "center" }} />
        <Typography>{status.name}</Typography>
      </Stack>
    </ToggleButton>
  );
};

export { IssuesFilterToggleButton };
